import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  size as floatingSize,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { Spinner, useDebounce } from "@visiblethread/react-components";
import clsx from "clsx";
import TextArea from "components/TextArea";
import UserAvatar from "components/UserAvatar";
import { bool, func, string } from "prop-types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMentionCandidates,
  selectIsMentionCandidatesLoading,
  selectMentionCandidates,
} from "slices/comments";
import { initialsFromName } from "utils/toInitials";

import classes from "./MentionTextarea.module.scss";

const MIN_QUERY_LENGTH = 0;
const DEBOUNCE_MS = 200;

const findActiveMentionRun = (text, caret) => {
  const uptoCaret = text.slice(0, caret);
  const atIndex = uptoCaret.lastIndexOf("@");
  if (atIndex === -1) {
    return null;
  }
  const query = uptoCaret.slice(atIndex + 1);
  if (/\s/.test(query)) {
    return null;
  }
  return { atIndex, query };
};

const MentionTextarea = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  autoFocus = false,
}) => {
  const dispatch = useDispatch();
  const candidates = useSelector(selectMentionCandidates);
  const isFetching = useSelector(selectIsMentionCandidatesLoading);
  const [mentionRun, setMentionRun] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isPendingFetch, setIsPendingFetch] = useState(false);
  const wrapperRef = useRef(null);
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (!autoFocus || hasAutoFocusedRef.current) {
      return;
    }
    hasAutoFocusedRef.current = true;
    wrapperRef.current?.querySelector("textarea")?.focus();
  }, [autoFocus]);

  const isLoading = isFetching || isPendingFetch;

  const debouncedFetchCandidates = useDebounce(
    (q) => {
      setIsPendingFetch(false);
      dispatch(fetchMentionCandidates({ q }));
    },
    DEBOUNCE_MS,
    [dispatch]
  );

  const isOpen =
    mentionRun !== null && mentionRun.query.length >= MIN_QUERY_LENGTH;

  const closeListbox = () => {
    setMentionRun(null);
    setIsPendingFetch(false);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) {
        closeListbox();
      }
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      floatingSize({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  useLayoutEffect(() => {
    const node = wrapperRef.current?.querySelector("textarea");
    if (node) {
      refs.setReference(node);
    }
  }, [refs]);

  const insertMention = (candidate) => {
    if (!mentionRun) {
      return;
    }
    const before = value.slice(0, mentionRun.atIndex);
    const after = value.slice(mentionRun.atIndex + 1 + mentionRun.query.length);
    const token = `@[${candidate.userName}]`;
    onChange(`${before}${token}${after}`);
    closeListbox();

    const caretPosition = before.length + token.length;
    requestAnimationFrame(() => {
      const node = wrapperRef.current?.querySelector("textarea");
      if (node) {
        node.focus();
        node.setSelectionRange(caretPosition, caretPosition);
      }
    });
  };

  const handleChange = (e) => {
    const text = e.target.value;
    const caret = e.target.selectionStart;
    onChange(text);

    const run = findActiveMentionRun(text, caret);
    setMentionRun(run);
    setHighlightedIndex(0);
    if (run && run.query.length >= MIN_QUERY_LENGTH) {
      setIsPendingFetch(true);
      debouncedFetchCandidates(run.query);
    } else {
      setIsPendingFetch(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (candidates.length > 0) {
        setHighlightedIndex((index) => (index + 1) % candidates.length);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (candidates.length > 0) {
        setHighlightedIndex(
          (index) => (index - 1 + candidates.length) % candidates.length
        );
      }
    } else if (e.key === "Enter") {
      if (candidates.length > 0) {
        e.preventDefault();
        insertMention(candidates[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeListbox();
    }
  };

  return (
    <div className={classes["mention_textarea"]} ref={wrapperRef}>
      <TextArea
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
      />
      {isOpen && (
        <FloatingPortal>
          <ul
            ref={refs.setFloating}
            style={floatingStyles}
            className={classes["mention_textarea__listbox"]}
            role="listbox"
            {...getFloatingProps()}
          >
            {isLoading && (
              <li className={classes["mention_textarea__state"]}>
                <Spinner size={20} />
              </li>
            )}
            {!isLoading && candidates.length === 0 && (
              <li
                className={clsx(
                  classes["mention_textarea__state"],
                  classes["mention_textarea__empty"]
                )}
              >
                No Matching Users
              </li>
            )}
            {!isLoading &&
              candidates.map((candidate, index) => (
                <li
                  key={candidate.userName}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={clsx(classes["mention_textarea__option"], {
                    [classes["mention_textarea__option--highlighted"]]:
                      index === highlightedIndex,
                  })}
                  onClick={() => insertMention(candidate)}
                >
                  <UserAvatar
                    initials={initialsFromName(candidate.displayName)}
                    name={candidate.displayName}
                  />
                  <span className={classes["mention_textarea__option__name"]}>
                    {candidate.displayName}
                  </span>
                  <span
                    className={classes["mention_textarea__option__username"]}
                  >
                    {candidate.userName}
                  </span>
                </li>
              ))}
          </ul>
        </FloatingPortal>
      )}
    </div>
  );
};

MentionTextarea.propTypes = {
  value: string.isRequired,
  onChange: func.isRequired,
  placeholder: string,
  ariaLabel: string,
  autoFocus: bool,
};

export default MentionTextarea;
